import type {
  GridColDef,
  GridRowParams,
  GridFilterModel,
  GridColumnMenuProps,
  GridSlots,
  GridSlotProps,
} from '@mui/x-data-grid-pro';
import { DataGridPro as MuiDataGrid, GridColumnMenu } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid-pro/locales';
import type {
  CollectionModel,
  EformModel,
  StandardModel,
  SubscriptionModel,
} from 'redux/services/api.generated';
import Toolbar from './Toolbar/Toolbar';
import i18n from 'i18n';
import type { Dispatch, SetStateAction } from 'react';
import Spinner from 'components/global/Spinner/Spinner';
import { useTranslation } from 'react-i18next';
import styles from './SubscriptionLinesDataGrid.module.css';

const norwegianLocale = {
  ...nbNO.components.MuiDataGrid.defaultProps.localeText,
};

function CustomColumnMenu(props: GridColumnMenuProps) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        // Remove user's ability to manage columns.
        columnMenuColumnsItem: null,
      }}
    />
  );
}

interface DataGridProps {
  readonly rows: StandardModel[] | CollectionModel[] | undefined;
  readonly columns: GridColDef[];
  readonly enableSearch?: boolean;
  readonly includeStandardsInCollections?: boolean;
  readonly setIncludeStandardsInCollections?: Dispatch<SetStateAction<boolean>>;
  readonly className?: string;
  readonly refetch: () => void;
  readonly filterModel: GridFilterModel;
  readonly setFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
  readonly datePickerMaxDate?: string;
  readonly subscriptionId: string;
  readonly subscriptionStatus?: SubscriptionModel['subscriptionStatus'];
  readonly name: 'standards' | 'collections' | 'eforms';
  readonly isFetching: boolean;
}

function SubscriptionLinesDataGrid({
  rows,
  columns,
  enableSearch,
  includeStandardsInCollections,
  setIncludeStandardsInCollections,
  className,
  refetch,
  filterModel,
  setFilterModel,
  datePickerMaxDate,
  subscriptionId,
  subscriptionStatus,
  name,
  isFetching,
}: DataGridProps) {
  const { t } = useTranslation();

  let dataGridStyles = styles.dataGrid;
  if (className) {
    dataGridStyles += ` ${className}`;
  }

  // TODO: Improve handling of missing subscriptionStatus (SM-146).
  if (!rows || !subscriptionStatus) {
    return <Spinner size="large" />;
  }
  const isEditable = !['Inactive', 'Discontinued'].includes(subscriptionStatus);

  return (
    <MuiDataGrid
      columns={columns}
      rows={rows}
      checkboxSelection={isEditable}
      disableRowSelectionOnClick
      isRowSelectable={(
        params: GridRowParams<StandardModel | CollectionModel | EformModel>,
      ) =>
        ['Standard', 'Collection', 'Eform'].includes(params.row.lineType) &&
        params.row.lineStatus !== 'Draft'
      }
      slots={{
        toolbar: Toolbar as GridSlots['toolbar'],
        columnMenu: CustomColumnMenu,
      }}
      slotProps={{
        toolbar: {
          enableSearch,
          includeStandardsInCollections,
          setIncludeStandardsInCollections,
          refetch,
          datePickerMaxDate,
          subscriptionId,
        } as GridSlotProps['toolbar'],
      }}
      className={dataGridStyles}
      density="compact"
      localeText={i18n.language === 'nb' ? norwegianLocale : undefined}
      filterModel={filterModel}
      onFilterModelChange={(newFilterModel) => setFilterModel(newFilterModel)}
      aria-label={t(`labels.${name}`)}
      loading={isFetching}
      showToolbar
    />
  );
}

export default SubscriptionLinesDataGrid;
